/* =============================================================================
 *  AttachmentPreviewModal
 *
 *  Full-screen (phones) / centred (desktop) preview of one email attachment:
 *    - images (PNG / JPEG / GIF / WebP) render in an <img> inside the modal;
 *    - PDFs do NOT render in a frame. The hosting CSP carries `object-src
 *      'none'` (firebase.json), which blocks a PDF viewer plugin in an
 *      <object>/<embed> and is unreliable for an <iframe>; iOS Safari and
 *      Android Chrome cannot show a multi-page PDF in a frame anyway. So the
 *      PDF opens in a NEW TAB: the tap handler calls openPreviewTab()
 *      synchronously (popup blockers / iOS need the user gesture), this modal
 *      fetches the bytes and navigates that tab to a blob: URL. The modal stays
 *      as the status + "open again" + download surface.
 *
 *  Safety:
 *    - the bytes are re-typed to the canonical MIME chosen by
 *      attachmentPreview.previewMime (image/png|jpeg|gif|webp or
 *      application/pdf) - the sender's own label is never trusted, and SVG /
 *      HTML are never previewable;
 *    - object URLs for images are revoked on close / unmount; a PDF URL must
 *      outlive the modal (the tab is still showing it, and its Save / Print
 *      buttons re-read the URL), so it is revoked after PDF_URL_TTL_MS.
 *
 *  Dismissal: [close] button, Esc, backdrop click (desktop) and the phone back
 *  gesture (one history entry is pushed while open, so Back closes the preview
 *  instead of the whole email panel underneath).
 * ============================================================================= */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatBytes } from '../utils/attachmentLimits';
import { PREVIEW_MAX_BYTES, safeFilename } from '../utils/attachmentPreview';
import './AttachmentPreviewModal.scss';

export interface PreviewTarget {
  /** Changes whenever a different attachment is opened (resets the modal). */
  key: string;
  kind: 'image' | 'pdf';
  filename: string;
  /** Canonical MIME from previewVerdict; the loaded bytes are re-typed to it. */
  mime: string;
  size?: number | null;
  /** Images only: an https URL shown directly (stored attachments). */
  url?: string;
  /** Authenticated byte fetch (Graph attachments). */
  load?: (signal: AbortSignal) => Promise<Blob>;
  /** PDFs only: the tab opened synchronously inside the tap handler. */
  pdfTab?: Window | null;
}

/** How long a PDF blob URL is kept after the modal closes. */
export const PDF_URL_TTL_MS = 10 * 60 * 1000;

/** Opens the (blank) tab a PDF will land in. MUST run synchronously inside the
 *  user's tap, before any await - otherwise popup blockers refuse it. */
export function openPreviewTab(): Window | null {
  try {
    const tab = window.open('', '_blank');
    if (!tab) return null;
    try { tab.opener = null; } catch { /* cross-origin already: fine */ }
    try {
      tab.document.title = 'Loading PDF';
      tab.document.body.textContent = 'Loading PDF...';
    } catch { /* ignore */ }
    return tab;
  } catch {
    return null;
  }
}

/** Open an already-loaded blob: URL in a tab (used by the [open pdf] button,
 *  which is a fresh tap, so a plain window.open is allowed). */
function openUrlInTab(url: string): boolean {
  try {
    const tab = window.open(url, '_blank');
    if (!tab) return false;
    try { tab.opener = null; } catch { /* ignore */ }
    return true;
  } catch {
    return false;
  }
}

const closeTabQuietly = (tab: Window | null | undefined) => {
  try { if (tab && !tab.closed) tab.close(); } catch { /* ignore */ }
};

const scheduleRevoke = (url: string, ms: number) => {
  window.setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* ignore */ } }, ms);
};

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

type Phase = 'loading' | 'ready' | 'error';

interface Props {
  target: PreviewTarget | null;
  onClose: () => void;
}

const AttachmentPreviewModal: React.FC<Props> = ({ target, onClose }) => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState('');
  const [src, setSrc] = useState<string | null>(null);
  const [imgState, setImgState] = useState<'loading' | 'ok' | 'broken'>('loading');
  const [pdfOpened, setPdfOpened] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // The tab handed in by the tap; consumed by the first load attempt only.
  const tabRef = useRef<Window | null>(null);
  const targetKey = target ? target.key : null;

  // A different attachment: take ownership of its tab.
  useEffect(() => {
    tabRef.current = target && target.kind === 'pdf' ? target.pdfTab || null : null;
    setAttempt(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey]);

  // -- Load the bytes ---------------------------------------------------------
  useEffect(() => {
    if (!target) return undefined;
    const t = target;
    setPhase('loading');
    setError('');
    setSrc(null);
    setImgState('loading');
    setPdfOpened(false);
    setZoomed(false);

    // Stored attachment: the https URL is shown as-is, nothing to fetch.
    if (t.kind === 'image' && t.url) {
      setSrc(t.url);
      setPhase('ready');
      return undefined;
    }

    const ctrl = new AbortController();
    let objectUrl: string | null = null;
    let pendingTab: Window | null = tabRef.current;
    tabRef.current = null;

    (async () => {
      try {
        if (!t.load) throw new Error('nothing to load');
        const raw = await t.load(ctrl.signal);
        if (ctrl.signal.aborted) return;
        if (raw.size === 0) throw new Error('the attachment is empty');
        if (raw.size > PREVIEW_MAX_BYTES) throw new Error('too large to preview - download it instead');
        // Re-type to OUR canonical MIME; the sender's label is never used.
        const blob = new Blob([raw], { type: t.mime });
        const url = URL.createObjectURL(blob);
        if (t.kind === 'pdf') {
          // The tab (if any) now owns the URL: it must outlive this modal.
          scheduleRevoke(url, PDF_URL_TTL_MS);
          let opened = false;
          if (pendingTab && !pendingTab.closed) {
            try { pendingTab.location.href = url; opened = true; } catch { opened = false; }
          }
          pendingTab = null;
          setPdfOpened(opened);
        } else {
          objectUrl = url;
        }
        setSrc(url);
        setPhase('ready');
      } catch (e) {
        if (ctrl.signal.aborted) return;
        closeTabQuietly(pendingTab);
        pendingTab = null;
        setError(e instanceof Error && e.message ? e.message : 'could not load the attachment');
        setPhase('error');
      }
    })();

    return () => {
      ctrl.abort();
      if (objectUrl) { try { URL.revokeObjectURL(objectUrl); } catch { /* ignore */ } }
      // Closed before the PDF arrived: don't leave an empty tab behind.
      closeTabQuietly(pendingTab);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, attempt]);

  // -- Esc + Tab trap (capture on window so the reader's own Esc / r / e
  //    handlers underneath never see the key) ---------------------------------
  useEffect(() => {
    if (!targetKey) return undefined;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.stopPropagation();
        ev.preventDefault();
        onCloseRef.current();
        return;
      }
      if (ev.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const items = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!items.length) { ev.preventDefault(); dialog.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (!active || !dialog.contains(active)) { ev.preventDefault(); first.focus(); return; }
      if (ev.shiftKey && active === first) { ev.preventDefault(); last.focus(); }
      else if (!ev.shiftKey && active === last) { ev.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [targetKey]);

  // -- Focus in, focus back out -------------------------------------------------
  useEffect(() => {
    if (!targetKey) return undefined;
    const before = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => { try { before?.focus?.(); } catch { /* ignore */ } };
  }, [targetKey]);

  // -- Phone back gesture closes the preview, not the whole panel ---------------
  // EmailSlidingPanel pushes one history entry and closes on popstate. Ours is a
  // second entry: a capture-phase popstate listener consumes the pop first
  // (stopImmediatePropagation) so the panel never sees it.
  useEffect(() => {
    if (!targetKey) return undefined;
    try { window.history.pushState({ attachmentPreview: true }, ''); } catch { return undefined; }
    let consumed = false;
    const onPop = (ev: PopStateEvent) => {
      consumed = true;
      ev.stopImmediatePropagation();
      onCloseRef.current();
    };
    window.addEventListener('popstate', onPop, true);
    return () => {
      window.removeEventListener('popstate', onPop, true);
      if (consumed) return;
      // Closed with the button / Esc / backdrop: drop our own entry, and keep
      // the resulting popstate away from the panel too.
      try {
        if ((window.history.state as { attachmentPreview?: boolean } | null)?.attachmentPreview === true) {
          const swallow = (ev: Event) => {
            ev.stopImmediatePropagation();
            window.removeEventListener('popstate', swallow, true);
          };
          window.addEventListener('popstate', swallow, true);
          window.setTimeout(() => window.removeEventListener('popstate', swallow, true), 1500);
          window.history.back();
        }
      } catch { /* ignore */ }
    };
  }, [targetKey]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  if (!target) return null;

  const name = safeFilename(target.filename);
  const sizeLabel = typeof target.size === 'number' && target.size > 0 ? formatBytes(target.size) : '';
  const isImage = target.kind === 'image';
  const isRemote = !!target.url && isImage;

  const download = src ? (
    <a
      className="att-preview__btn"
      href={src}
      download={name}
      {...(isRemote ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      [download]
    </a>
  ) : null;

  let stage: React.ReactNode;
  if (phase === 'error') {
    stage = (
      <div className="att-preview__status att-preview__status--error" role="alert">
        <div>couldn&apos;t load the preview: {error}</div>
        {target.load && (
          <button type="button" className="att-preview__btn att-preview__btn--primary" onClick={retry}>
            [retry]
          </button>
        )}
      </div>
    );
  } else if (isImage) {
    stage = (
      <>
        {(phase === 'loading' || imgState === 'loading') && imgState !== 'broken' && (
          <div className="att-preview__status" role="status">loading preview...</div>
        )}
        {imgState === 'broken' && (
          <div className="att-preview__status att-preview__status--error" role="alert">
            couldn&apos;t display this image - download it instead
          </div>
        )}
        {src && imgState !== 'broken' && (
          <img
            className={`att-preview__img${zoomed ? ' att-preview__img--zoomed' : ''}${imgState === 'ok' ? '' : ' att-preview__img--pending'}`}
            src={src}
            alt={name}
            draggable={false}
            referrerPolicy="no-referrer"
            onLoad={() => setImgState('ok')}
            onError={() => setImgState('broken')}
            onClick={() => setZoomed((z) => !z)}
          />
        )}
      </>
    );
  } else if (phase === 'loading') {
    stage = <div className="att-preview__status" role="status">fetching the PDF...</div>;
  } else {
    stage = (
      <div className="att-preview__status" role="status">
        <div>{pdfOpened ? 'opened in a new tab' : 'your PDF is ready'}</div>
        <button
          type="button"
          className="att-preview__btn att-preview__btn--primary"
          onClick={() => {
            // Fresh tap: a plain window.open is allowed here.
            if (src && openUrlInTab(src)) setPdfOpened(true);
          }}
        >
          {pdfOpened ? '[open again]' : '[open pdf]'}
        </button>
        <div className="att-preview__hint">
          PDFs open in their own tab so your browser&apos;s viewer can zoom, search and print them.
        </div>
      </div>
    );
  }

  return createPortal(
    <div
      className="att-preview"
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="att-preview__panel"
        role="dialog"
        aria-modal="true"
        aria-label={`Preview of ${name}`}
        ref={dialogRef}
        tabIndex={-1}
      >
        <div className="att-preview__bar">
          <span className="att-preview__name" title={name}>
            {name}
            {sizeLabel && <span className="att-preview__meta"> {sizeLabel}</span>}
          </span>
          {isImage && phase === 'ready' && imgState === 'ok' && (
            <button
              type="button"
              className="att-preview__btn"
              onClick={() => setZoomed((z) => !z)}
              aria-pressed={zoomed}
            >
              {zoomed ? '[fit]' : '[zoom]'}
            </button>
          )}
          {download}
          <button
            type="button"
            className="att-preview__btn att-preview__close"
            onClick={onClose}
            ref={closeRef}
            aria-label="Close preview"
          >
            [close]
          </button>
        </div>
        <div className={`att-preview__stage${isImage ? ' att-preview__stage--image' : ''}`}>{stage}</div>
      </div>
    </div>,
    document.body,
  );
};

export default AttachmentPreviewModal;
